import React, { useCallback, useEffect, useMemo , useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Dialog, IconButton, Slide, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { CloseIcon } from "src/Icons";
import { getCategoryTemplates } from "src/store/reducers/categoryTemplatesSlice";
import {
  closeServicesDialog,
  resetServicesDialogParams,
} from "src/store/reducers/dialogSlice";
import { closeModal, showModal } from "src/store/reducers/modalSlice";
import { createServices, updateServices } from "src/store/reducers/servicesSlice";
import { createServiceTemplate, updateServiceTemplate } from "src/store/reducers/serviceTemplatesSlice";
import { getStudios } from "src/store/reducers/studiosSlice";
import { generalStyles } from "src/utils/generalStyles/generalStyles";
import { priceNormalizer } from "src/utils/helpers";
import { PriceActionTypes } from "src/utils/helpers/helpers.types";
import { InfoString } from "src/utils/infoString";
import { isTitle } from "src/utils/validation";

import { SubmitButton } from "../../Button/SubmitButton";
import { AvatarSelect } from "../../ImageSelect/AvatarSelect";
import { DefaultInput } from "../../Input/DefaultInput";
import { PriceInput } from "../../Input/PriceInput";
import { TimeInput } from "../../Input/TimeInput";
import { DefaultSingleSelector } from "../../Selector/DefaultSingleSelector";

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

export enum PriceTypes {
  NoRank,
  Junior,
  Master,
  Top,
}

export const ServicesDialog = ({ isOpen }: any) => {
  const dispatch = useDispatch();

  const { servicesDialogParams: params } = useSelector(
    (state: any) => state.dialog
  );
  const { categories: categoryTemplates } = useSelector((state: any) => state.categoryTemplates);
  const { studio: selectedStudio} = useSelector((state: any) => state.selectedStudio);

  const categories = selectedStudio ? selectedStudio.categories : categoryTemplates;

  const formRef = useRef<any>();
  const avatarRef = useRef<any>();

  const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);

  const [image, setImage] = useState<string>(params?.data?.image);
  const [title, setTitle] = useState<string>(params?.data?.title);
  // category, subcategory
  const [assignedCatId, setAssignedCatId] = useState<any>(params?.data?.category?.id || '');
  const currentCategory = categories.find(
    (category: { id: any}) => +category.id === +assignedCatId
  );
  const [assignedSubCatId, setAssignedSubCatId] = useState<any>(params.data.subCategory?.id || '');
  const [subCategories, setSubCategories] = useState<any[]>(
    currentCategory ? [{
      id: '0',
      label: 'Without Subcategory',
      imageSrc: '',
    }, ...currentCategory.children.map((subcategory: any) => ({
      id: subcategory.id,
      imageSrc: subcategory.image,
      label: subcategory.title,
    }))] : [])
  // rank
  const [noRank, setNoRank] = useState(
    params.data.priceList[PriceTypes.NoRank] ?
      priceNormalizer(params.data.priceList[PriceTypes.NoRank].price, PriceActionTypes.Showing) : 0
  );
  const [junior, setJunior] = useState(
    params.data.priceList[PriceTypes.Junior] ?
      priceNormalizer(params.data.priceList[PriceTypes.Junior].price, PriceActionTypes.Showing) : 0
  );
  const [master, setMaster] = useState(
    params.data.priceList[PriceTypes.Master] ?
      priceNormalizer(params.data.priceList[PriceTypes.Master].price, PriceActionTypes.Showing) : 0
  );
  const [top, setTop] = useState(
    params.data.priceList[PriceTypes.Top] ?
      priceNormalizer(params.data.priceList[PriceTypes.Top].price, PriceActionTypes.Showing) : 0
  );

  const [duration, setDuration] = useState<number>(params.data.duration);

  const [hours, setHours] = useState<number>(Math.floor(duration / 60));
  const [minutes, setMinutes] = useState<number>(
    Math.floor(duration - hours * 60)
  );

  const adaptedCategories = categories
    .map(({ id, image, title, children }: any) => ({
      id,
      label: title,
      imageSrc: image,
      subcategories: children
    }));

  const handleChangeImage = useCallback((newImage: string) => {
    setImage(newImage);
  },[]);

  const handleChangeTitle = useCallback((newValue: string) => {
    setTitle(newValue);
  },[]);

  const getSubcategoriesByCategoryId = (id: number) => {
    const foundCategory = adaptedCategories.find((category: any ) => category.id === id);
    const newSubCategories = [{
      id: '0',
      label: 'Without Subcategory',
      imageSrc: '',
    },
    ...foundCategory?.subcategories?.map((subcategory: any) => ({
      id: subcategory.id,
      imageSrc: subcategory.image,
      label: subcategory.title,
    }))];
    setSubCategories(newSubCategories)
  };

  const handleChangeSelectCategories = (value: string) => {
    setAssignedCatId(+value);
    setAssignedSubCatId('')
    getSubcategoriesByCategoryId(+value)
  };

  const handleChangeNoRank = useCallback((e:any) => {
    setNoRank(e)
  },[])

  const handleChangeJunior = useCallback((e:any) => {
    setJunior(e)
  },[])

  const handleChangeMaster = useCallback((e:any) => {
    setMaster(e)
  },[])

  const handleChangeTop = useCallback((e:any) => {
    setTop(e)
  },[])

  const handleChangeMinutes = useCallback((newValue:any) => {
    setMinutes(newValue);
  },[])

  const handleChangeHours = useCallback((newValue:any) => {
    setHours(newValue);
  },[])

  const handleChangeSelectSubCategories = (value: string) => {
    setAssignedSubCatId(+value)
  };

  const isContinueButtonAvailable = useMemo(() => {
    const isBasicInfoStepFulfilled =
      isTitle(title) &&
      currentCategory &&
      image !== '' &&
      noRank > 0 &&
      junior > 0 &&
      master > 0 &&
      top > 0 &&
      (hours > 0 || minutes > 0);

    return isBasicInfoStepFulfilled;
  }, [
    title,
    currentCategory,
    image,
    noRank,
    junior,
    master,
    top,
    hours,
    minutes
  ]);

  const resetDialogData = () => {
    setTitle('');
    setAssignedCatId('');
    setNoRank(0);
    setJunior(0);
    setMaster(0);
    setTop(0);
    setHours(0);
    setMinutes(0);
  };

  const onClose = () => {
    resetDialogData();
    dispatch(resetServicesDialogParams());
    dispatch(closeServicesDialog());
  };

  const handleSaveButtonPressConditionaly = () => {
    const createAction = selectedStudio ? createServices : createServiceTemplate;
    const updateAction = selectedStudio ? updateServices : updateServiceTemplate;
    const successAction = selectedStudio ? getStudios : getCategoryTemplates;
    const conditionalParams = selectedStudio ? { studioId: selectedStudio?.id } : {};

    if (params.isEdit) {
      setIsSaveButtonLoading(true);
      dispatch(
        updateAction({
          ...conditionalParams,
          serviceId: params.data.id,
          title: params.data.title !== title ? title : undefined,
          image: params.data.image !== image ? image : undefined,
          duration:
            +params.data.duration !== +(hours * 60 + minutes)
              ? +(hours * 60 + minutes)
              : undefined,
          priceList: [
            { rank: "none", price: priceNormalizer(noRank, PriceActionTypes.Sending) },
            { rank: "junior", price: priceNormalizer(junior, PriceActionTypes.Sending) },
            { rank: "master", price: priceNormalizer(master, PriceActionTypes.Sending) },
            { rank: "top", price: priceNormalizer(top, PriceActionTypes.Sending) },
          ],
          categories: assignedSubCatId ? [+assignedSubCatId] : [+assignedCatId],
          onSuccess: () => {
            setIsSaveButtonLoading(false);
            dispatch(successAction())
            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: "Changes successfully saved",
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
          },
          onFailure: (error: any) => {
            setIsSaveButtonLoading(false);
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    } else {
      setIsSaveButtonLoading(true);
      dispatch(
        createAction({
          ...conditionalParams,
          title,
          image,
          duration: +(hours * 60 + minutes),
          priceList: [
            { rank: "none", price: priceNormalizer(noRank, PriceActionTypes.Sending) },
            { rank: "junior", price: priceNormalizer(junior, PriceActionTypes.Sending) },
            { rank: "master", price: priceNormalizer(master, PriceActionTypes.Sending) },
            { rank: "top", price: priceNormalizer(top, PriceActionTypes.Sending) },
          ],
          categories: assignedSubCatId ? [+assignedSubCatId] : [+assignedCatId],
          onSuccess: () => {
            setIsSaveButtonLoading(false);
            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: `Service ${title} successfully created`,
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(successAction());
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
          },
          onFailure: (error: any) => {
            setIsSaveButtonLoading(false);
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    }
  }

  useEffect(() => {
    if (!params.data.selectedStudio) {
      dispatch(getCategoryTemplates());
    }
  }, []);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      TransitionComponent={Transition}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          width: "100%",
          backgroundColor: "#fafafa",
          padding: "16px 40px",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignContent="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            marginBottom: "16px",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: "#F3F3F3",
              color: "#676767",
              fontSize: "26px",
            }}
            disableRipple
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Box>
            <Typography
              sx={{
                fontSize: "26px",
                fontWeight: "600",
              }}
            >
              {params.isEdit ? "Edit service" : "Create new service"}
            </Typography>
          </Box>

          <SubmitButton
            title={params.isEdit ? "Save changes" : "Create service"}
            isLoading={isSaveButtonLoading}
            disabled={!isContinueButtonAvailable}
            onClick={handleSaveButtonPressConditionaly}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginLeft: "-3%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "56px 16px 0",
            width: "761px",
            minWidth: "400px",
          }}
        >
          <Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "48px",
                }}
              >
                Basic information
              </Typography>
              <AvatarSelect
                ref={avatarRef}
                image={image}
                onImageChanged={handleChangeImage}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "400",
                  color: "#828282",
                  marginTop: "16px",
                  marginBottom: "40px",
                }}
              >
                Allowed file types
                : png, jpg, jpeg.
              </Typography>
            </Box>

            <Box>
              <form ref={formRef}>
                <DefaultInput
                  initialValue={title}
                  placeholder="service"
                  label="Services name"
                  onInputValueChange={handleChangeTitle}
                  validationFn={isTitle}
                  sx={generalStyles.borderRadiusAll}
                />
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginTop: "56px",
                    marginBottom: "24px",
                  }}
                >
                  Service category
                </Typography>

                { (adaptedCategories && adaptedCategories.length > 0) &&
                  <>
                    <DefaultSingleSelector
                      placeholder="category"
                      value={assignedCatId}
                      valuesToSelect={adaptedCategories}
                      onChanged={handleChangeSelectCategories}
                      title="Category"
                      leftContainerStyle={
                        (assignedCatId && subCategories?.length) > 0 ?
                          generalStyles.leftSingleContainerWithUnBottomRadius :
                          generalStyles.leftSingleContainer
                      }
                      rightContainerStyle={
                        (assignedCatId && subCategories?.length) > 0 ?
                          generalStyles.rightSingleContainerUnBottomRadius :
                          generalStyles.rightSingleContainer
                      }
                    />
                    { (assignedCatId && subCategories && subCategories.length > 1) &&
                      <DefaultSingleSelector
                        placeholder="subcategory"
                        value={assignedSubCatId}
                        valuesToSelect={subCategories}
                        onChanged={handleChangeSelectSubCategories}
                        title="Subcategory"
                        containerStyle={generalStyles.marginTopBotOne}
                        leftContainerStyle={
                          generalStyles.leftSingleContainerWithUnTopRadius
                        }
                        rightContainerStyle={
                          generalStyles.rightSingleContainerUnTopRadius
                        }
                      />
                    }
                    <InfoString child='Each service must belong to category, while subcategory is not required'/>
                  </>
                }

                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginTop: "56px",
                    marginBottom: "24px",
                  }}
                >
                  Price
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <PriceInput
                    label="No rank"
                    value={noRank}
                    onInputPriceChange={handleChangeNoRank}
                    sx={generalStyles.borderTopLeftRadius}
                  />
                  <PriceInput
                    value={master}
                    label="Master"
                    onInputPriceChange={handleChangeMaster}
                    sx={generalStyles.borderTopRightRadius}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    marginTop: "-1px",
                  }}
                >
                  <PriceInput
                    value={junior}
                    label="Junior"
                    onInputPriceChange={handleChangeJunior}
                    sx={generalStyles.borderBottomLeftRadius}
                  />
                  <PriceInput
                    value={top}
                    label="Top"
                    onInputPriceChange={handleChangeTop}
                    sx={generalStyles.borderBottomRightRadius}
                  />
                </Box>

                <InfoString child="You have to enter at least one price type"/>

                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginTop: "56px",
                    marginBottom: "24px",
                  }}
                >
                  Duration
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <TimeInput
                    label="Hours"
                    value={hours}
                    placeholder="0"
                    maxCount={12}
                    minCount={0}
                    onChange={handleChangeHours}
                    sx={generalStyles.borderTopBottomLeftRadius}
                  />
                  <TimeInput
                    label="Minutes"
                    placeholder="00"
                    value={minutes}
                    maxCount={59}
                    minCount={0}
                    onChange={handleChangeMinutes}
                    sx={generalStyles.borderTopBottomRightRadius}
                  />
                </Box>

                <InfoString child={`Total duration ${hours}h ${minutes}min`}/>

              </form>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
